import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Settings, 
  GripVertical, 
  ChevronUp, 
  ChevronDown, 
  Save,
  RotateCcw,
  Eye,
  EyeOff,
  ChevronRight,
  Plus,
  Trash2
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { useAuth, API } from '../../contexts/AuthContext';


// Pestañas que el panel conoce. Es la misma lista que AdminDashboard usa por
// defecto; si el menu guardado en el servidor es ANTERIOR a alguna (Sincronizacion
// lo es), completarPestanasNuevas la agrega al cargar para que se pueda ver y
// activar/desactivar desde aqui sin re-plantar el menu de cada tenant a mano.
const DEFAULT_MENU_ITEMS = [
  { id: 'myrestaurant', name: 'Mi Restaurante', icon: 'Settings', visible: true, order: 1, module: 'config' },
  { id: 'categories', name: 'Categorías', icon: 'Tag', visible: true, order: 2, module: 'categories' },
  { id: 'printers', name: 'Impresoras', icon: 'Printer', visible: true, order: 3, module: 'printers' },
  {
    id: 'products',
    name: 'Productos',
    icon: 'Package',
    visible: true,
    order: 4,
    module: 'products',
    hasSubmenu: true,
    submenu: [
      { id: 'products-list', name: 'Productos', icon: 'Package', visible: true, parentId: 'products', module: 'products' },
      { id: 'ingredients', name: 'Ingredientes', icon: 'Package', visible: true, parentId: 'products', module: 'inventory' },
      { id: 'inventory', name: 'Inventario', icon: 'Boxes', visible: true, parentId: 'products', module: 'inventory' },
      { id: 'purchases', name: 'Compras', icon: 'ShoppingCart', visible: true, parentId: 'products', module: 'purchases' },
      { id: 'recipes', name: 'Recetas', icon: 'ChefHat', visible: true, parentId: 'products', module: 'products' }
    ]
  },
  { id: 'tables', name: 'Mesas', icon: 'Users', visible: true, order: 5, module: 'tables' },
  { id: 'orders', name: 'Órdenes', icon: 'FileText', visible: true, order: 6, module: 'orders' },
  { id: 'reservations', name: 'Reservas', icon: 'FileText', visible: true, order: 7, module: 'reservations' },
  { id: 'loyalty', name: 'Fidelidad', icon: 'Gift', visible: true, order: 8, module: 'customers' },
  { id: 'reports', name: 'Reportes', icon: 'Settings', visible: true, order: 9, module: 'reports' },
  { id: 'accounts-receivable', name: 'Cuentas por Cobrar', icon: 'Wallet', visible: true, order: 10, module: 'customers' },
  { id: 'coupons', name: 'Cupones', icon: 'Ticket', visible: true, order: 11, module: 'config' },
  { id: 'cache-stats', name: 'Caché Redis', icon: 'Database', visible: true, order: 12, module: 'config' },
  { id: 'menuconfig', name: 'Config. Menú', icon: 'Settings', visible: true, order: 13, isSystem: true, module: 'config' },
  { id: 'permissions', name: 'Permisos', icon: 'Shield', visible: true, order: 14, module: 'config' },
  { id: 'sync', name: 'Sincronización', icon: 'RefreshCw', visible: true, order: 15, module: 'config' },
  { id: 'users', name: 'Usuarios', icon: 'Users', visible: true, order: 16, module: 'users' },
];

const cloneDefaults = () => DEFAULT_MENU_ITEMS.map((item) => ({
  ...item,
  submenu: item.submenu ? item.submenu.map((sub) => ({ ...sub })) : item.submenu,
}));

// Pestañas retiradas del menú: "Impuestos" ya no afecta a las órdenes (el ITBIS
// es fijo 18% y se activa/desactiva con el toggle del superadmin), así que se
// oculta también en este editor y se descarta de cualquier config ya guardada.
const HIDDEN_MENU_IDS = ['taxes'];

const completarPestanasNuevas = (config) => {
  const base = (Array.isArray(config) ? config : []).filter((item) => !HIDDEN_MENU_IDS.includes(item?.id));
  const defaults = cloneDefaults();
  const conocidos = new Set(base.map((item) => item?.id));
  const merged = [...base, ...defaults.filter((item) => item?.id && !conocidos.has(item.id))];
  // Sincronizar también los submenús: si un padre ya guardado no trae un subítem
  // nuevo (p.ej. Inventario), se agrega desde la definición por defecto siguiendo
  // el orden canónico, sin tocar los subítems ya persistidos.
  const defaultsById = new Map(defaults.map((item) => [item?.id, item]));
  return merged.map((item) => {
    const def = defaultsById.get(item?.id);
    if (!def || !def.hasSubmenu || !Array.isArray(def.submenu)) return item;
    const currentSub = Array.isArray(item.submenu) ? item.submenu : [];
    const currentById = new Map(currentSub.map((s) => [s?.id, s]));
    const ordered = def.submenu.map((s) => currentById.get(s.id) || s);
    const defSubIds = new Set(def.submenu.map((s) => s?.id));
    const extra = currentSub.filter((s) => s?.id && !defSubIds.has(s.id));
    const finalSub = [...ordered, ...extra];
    const changed = finalSub.length !== currentSub.length
      || finalSub.some((s, i) => s?.id !== currentSub[i]?.id);
    return changed ? { ...item, submenu: finalSub } : item;
  });
};

const MenuConfigTab = () => {
  const { getAuthHeaders } = useAuth();
  const [menuItems, setMenuItems] = useState(cloneDefaults);

  const [hasChanges, setHasChanges] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['products']);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverItem, setDraggedOverItem] = useState(null);
  const [draggedSubmenu, setDraggedSubmenu] = useState(null); // { parentIndex, submenuIndex }
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newMenu, setNewMenu] = useState({ name: '', icon: 'Settings' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMenuConfig();
  }, []);

  const loadMenuConfig = async () => {
    try {
      const response = await axios.get(`${API}/menu-config`, {
        headers: getAuthHeaders()
      });
      
      if (response.data && response.data.config) {
        // Un menu guardado antes de existir una pestana (Sincronizacion, p.ej.)
        // no la trae: se completa con la definicion local para que el admin pueda
        // verla y activarla/desactivarla desde aqui.
        const completa = completarPestanasNuevas(response.data.config);
        setMenuItems(completa);
        // También guardar en localStorage como respaldo
        localStorage.setItem('menuConfig', JSON.stringify(completa));
      }
    } catch (error) {
      console.error('Error loading menu config from server:', error);
      // Si falla, intentar cargar desde localStorage
      const savedConfig = localStorage.getItem('menuConfig');
      if (savedConfig) {
        try {
          setMenuItems(completarPestanasNuevas(JSON.parse(savedConfig)));
        } catch (parseError) {
          console.error('Error parsing localStorage config:', parseError);
        }
      }
    }
  };

  const toggleExpand = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  // Drag and Drop handlers for main menus
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    setDraggedSubmenu(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    // Visual feedback
    e.target.style.opacity = '0.4';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOverItem(index);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    
    if (draggedItem !== null && draggedOverItem !== null && draggedItem !== draggedOverItem) {
      const newItems = [...menuItems];
      const draggedItemContent = newItems[draggedItem];
      
      // Remove from old position
      newItems.splice(draggedItem, 1);
      
      // Insert at new position
      const targetIndex = draggedItem < draggedOverItem ? draggedOverItem - 1 : draggedOverItem;
      newItems.splice(targetIndex, 0, draggedItemContent);
      
      // Update order
      newItems.forEach((item, idx) => {
        item.order = idx + 1;
      });
      
      setMenuItems(newItems);
      setHasChanges(true);
      toast.success('Menú reordenado');
    }
    
    setDraggedItem(null);
    setDraggedOverItem(null);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Solo limpiar si realmente salimos del elemento
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDraggedOverItem(null);
    }
  };

  // Drag and Drop handlers for submenus
  const handleSubmenuDragStart = (e, parentIndex, submenuIndex) => {
    e.stopPropagation();
    setDraggedSubmenu({ parentIndex, submenuIndex });
    setDraggedItem(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    e.target.style.opacity = '0.4';
  };

  const handleSubmenuDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSubmenuDrop = (e, targetParentIndex, targetSubmenuIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedSubmenu) return;
    
    const { parentIndex: sourceParentIndex, submenuIndex: sourceSubmenuIndex } = draggedSubmenu;
    
    // Si es el mismo lugar, no hacer nada
    if (sourceParentIndex === targetParentIndex && sourceSubmenuIndex === targetSubmenuIndex) {
      return;
    }
    
    const newItems = [...menuItems];
    const sourceParent = newItems[sourceParentIndex];
    const targetParent = newItems[targetParentIndex];
    
    // Obtener el submenú arrastrado
    const draggedSubmenuItem = sourceParent.submenu[sourceSubmenuIndex];
    
    // Remover del origen
    sourceParent.submenu.splice(sourceSubmenuIndex, 1);
    
    // Si es el mismo padre, ajustar índice si es necesario
    const adjustedTargetIndex = 
      sourceParentIndex === targetParentIndex && sourceSubmenuIndex < targetSubmenuIndex
        ? targetSubmenuIndex - 1
        : targetSubmenuIndex;
    
    // Insertar en el destino
    if (!targetParent.submenu) {
      targetParent.submenu = [];
      targetParent.hasSubmenu = true;
    }
    
    // Actualizar parentId
    draggedSubmenuItem.parentId = targetParent.id;
    
    targetParent.submenu.splice(adjustedTargetIndex, 0, draggedSubmenuItem);
    
    // Limpiar submenu vacío
    if (sourceParent.submenu.length === 0) {
      delete sourceParent.submenu;
      sourceParent.hasSubmenu = false;
    }
    
    setMenuItems(newItems);
    setHasChanges(true);
    toast.success('Submenú movido');
  };

  const handleSubmenuDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedSubmenu(null);
  };

  // Permitir drop en menús principales para convertir submenús en menús principales
  const handleMenuDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Si estamos arrastrando un submenú, convertirlo en menú principal
    if (draggedSubmenu) {
      const { parentIndex, submenuIndex } = draggedSubmenu;
      const newItems = [...menuItems];
      const sourceParent = newItems[parentIndex];
      const submenuItem = sourceParent.submenu[submenuIndex];
      
      // Crear un nuevo menú principal desde el submenú
      const newMainMenu = {
        id: submenuItem.id,
        name: submenuItem.name,
        icon: submenuItem.icon || 'Settings',
        visible: submenuItem.visible,
        order: targetIndex + 1,
        isCustom: submenuItem.isCustom,
        module: submenuItem.module
      };
      
      // Remover del submenú original
      sourceParent.submenu.splice(submenuIndex, 1);
      if (sourceParent.submenu.length === 0) {
        delete sourceParent.submenu;
        sourceParent.hasSubmenu = false;
      }
      
      // Insertar como menú principal
      newItems.splice(targetIndex, 0, newMainMenu);
      
      // Actualizar orden
      newItems.forEach((item, idx) => {
        item.order = idx + 1;
      });
      
      setMenuItems(newItems);
      setHasChanges(true);
      toast.success(`"${submenuItem.name}" convertido en menú principal`);
      
      setDraggedSubmenu(null);
    }
  };

  const createNewMenu = () => {
    if (!newMenu.name.trim()) {
      toast.error('El nombre del menú es requerido');
      return;
    }

    // Generar ID único basado en el nombre
    const id = newMenu.name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/[^a-z0-9]+/g, '-') // Reemplazar espacios y caracteres especiales
      .replace(/^-+|-+$/g, ''); // Remover guiones al inicio/final

    // Verificar si ya existe
    if (menuItems.some(item => item.id === id)) {
      toast.error('Ya existe un menú con ese nombre');
      return;
    }

    const newMenuItem = {
      id,
      name: newMenu.name.trim(),
      icon: newMenu.icon,
      visible: true,
      order: menuItems.length + 1,
      isCustom: true // Marcar como personalizado para poder eliminarlo
    };

    setMenuItems([...menuItems, newMenuItem]);
    setHasChanges(true);
    setShowCreateDialog(false);
    setNewMenu({ name: '', icon: 'Settings' });
    toast.success('Menú creado exitosamente');
  };

  const deleteMenu = (id) => {
    const menu = menuItems.find(m => m.id === id);
    if (!menu.isCustom) {
      toast.error('No puedes eliminar menús del sistema');
      return;
    }

    if (window.confirm(`¿Estás seguro de eliminar el menú "${menu.name}"?`)) {
      const newItems = menuItems.filter(item => item.id !== id);
      // Actualizar orden
      newItems.forEach((item, idx) => {
        item.order = idx + 1;
      });
      setMenuItems(newItems);
      setHasChanges(true);
      toast.success('Menú eliminado');
    }
  };

  const convertMenuToSubmenu = (menuId, targetParentId) => {
    const menuToConvert = menuItems.find(m => m.id === menuId);
    if (!menuToConvert) return;

    // Crear el submenú a partir del menú principal
    const newSubmenu = {
      id: menuToConvert.id,
      name: menuToConvert.name,
      icon: menuToConvert.icon,
      visible: menuToConvert.visible,
      parentId: targetParentId,
      isCustom: menuToConvert.isCustom
    };

    // Si el menú a convertir tiene submenús, los mantenemos como propiedades pero no como submenús anidados
    // (para simplicidad, no permitimos submenús de submenús)

    const newItems = menuItems.map(item => {
      if (item.id === targetParentId) {
        return {
          ...item,
          hasSubmenu: true,
          submenu: [...(item.submenu || []), newSubmenu]
        };
      }
      return item;
    }).filter(item => item.id !== menuId); // Remover el menú original

    // Actualizar orden
    newItems.forEach((item, idx) => {
      item.order = idx + 1;
    });

    setMenuItems(newItems);
    setHasChanges(true);
    toast.success(`"${menuToConvert.name}" movido como submenú de "${menuItems.find(m => m.id === targetParentId)?.name}"`);
  };

  const moveSubmenuUp = (parentIndex, submenuIndex) => {
    if (submenuIndex === 0) return;
    
    const newItems = [...menuItems];
    const submenu = [...newItems[parentIndex].submenu];
    [submenu[submenuIndex - 1], submenu[submenuIndex]] = [submenu[submenuIndex], submenu[submenuIndex - 1]];
    newItems[parentIndex].submenu = submenu;
    
    setMenuItems(newItems);
    setHasChanges(true);
  };

  const moveSubmenuDown = (parentIndex, submenuIndex) => {
    const submenu = menuItems[parentIndex].submenu;
    if (submenuIndex === submenu.length - 1) return;
    
    const newItems = [...menuItems];
    const newSubmenu = [...newItems[parentIndex].submenu];
    [newSubmenu[submenuIndex], newSubmenu[submenuIndex + 1]] = [newSubmenu[submenuIndex + 1], newSubmenu[submenuIndex]];
    newItems[parentIndex].submenu = newSubmenu;
    
    setMenuItems(newItems);
    setHasChanges(true);
  };

  const toggleSubmenuVisibility = (parentId, submenuId) => {
    setMenuItems(menuItems.map(item => {
      if (item.id === parentId && item.submenu) {
        return {
          ...item,
          submenu: item.submenu.map(sub => 
            sub.id === submenuId ? { ...sub, visible: !sub.visible } : sub
          )
        };
      }
      return item;
    }));
    setHasChanges(true);
  };

  const moveSubmenuToParent = (fromParentId, submenuId, toParentId) => {
    const newItems = menuItems.map(item => {
      // Remover del padre original
      if (item.id === fromParentId && item.submenu) {
        return {
          ...item,
          submenu: item.submenu.filter(sub => sub.id !== submenuId)
        };
      }
      // Agregar al nuevo padre
      if (item.id === toParentId) {
        const submenuToMove = menuItems
          .find(m => m.id === fromParentId)
          ?.submenu?.find(sub => sub.id === submenuId);
        
        if (submenuToMove) {
          return {
            ...item,
            hasSubmenu: true,
            submenu: [...(item.submenu || []), { ...submenuToMove, parentId: toParentId }]
          };
        }
      }
      return item;
    });
    
    setMenuItems(newItems);
    setHasChanges(true);
    toast.success('Submenú movido exitosamente');
  };

  const moveUp = (index) => {
    if (index === 0) return;
    
    const newItems = [...menuItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    
    // Actualizar orden
    newItems.forEach((item, idx) => {
      item.order = idx + 1;
    });
    
    setMenuItems(newItems);
    setHasChanges(true);
  };

  const moveDown = (index) => {
    if (index === menuItems.length - 1) return;
    
    const newItems = [...menuItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    
    // Actualizar orden
    newItems.forEach((item, idx) => {
      item.order = idx + 1;
    });
    
    setMenuItems(newItems);
    setHasChanges(true);
  };

  const toggleVisibility = (id) => {
    setMenuItems(menuItems.map(item => 
      item.id === id ? { ...item, visible: !item.visible } : item
    ));
    setHasChanges(true);
  };

  const updateName = (id, newName) => {
    setMenuItems(menuItems.map(item => 
      item.id === id ? { ...item, name: newName } : item
    ));
    setHasChanges(true);
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/menu-config`,
        { config: menuItems },
        { headers: getAuthHeaders() }
      );
      
      // También guardar en localStorage como respaldo
      localStorage.setItem('menuConfig', JSON.stringify(menuItems));
      
      // Disparar evento personalizado para notificar a otros componentes
      window.dispatchEvent(new CustomEvent('menuConfigChanged'));
      
      toast.success('Configuración del menú guardada exitosamente.');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving menu config:', error);
      toast.error('Error al guardar la configuración del menú');
    } finally {
      setLoading(false);
    }
  };

  const resetConfig = () => {
    if (window.confirm('¿Estás seguro de restaurar la configuración predeterminada del menú?')) {
      // Misma lista que el panel renderiza por defecto (unica fuente de verdad).
      const defaultConfig = cloneDefaults();

      setMenuItems(defaultConfig);
      localStorage.setItem('menuConfig', JSON.stringify(defaultConfig));
      // Disparar evento personalizado para notificar a otros componentes
      window.dispatchEvent(new CustomEvent('menuConfigChanged'));
      toast.success('Configuración restaurada y aplicada.');
      setHasChanges(false);
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-orange-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Configuración del Menú</h2>
            <p className="text-sm text-gray-600">Personaliza el orden y visibilidad de las opciones del menú</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowCreateDialog(true)}
            className="rounded-xl border-green-500 text-green-600 hover:bg-green-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Crear Menú
          </Button>
          
          <Button
            variant="outline"
            onClick={resetConfig}
            className="rounded-xl"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Restaurar
          </Button>
          
          <Button
            onClick={saveConfig}
            disabled={!hasChanges || loading}
            className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl flex items-center gap-2">
          <Settings className="w-4 h-4 text-yellow-600" />
          <p className="text-sm text-yellow-700">
            Tienes cambios sin guardar. Presiona &quot;Guardar Cambios&quot; para aplicarlos.
          </p>
        </div>
      )}

      <Card className="divide-y">
        {menuItems.map((item, index) => (
          <div key={item.id}>
            {/* Menú Principal */}
            <div 
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleMenuDrop(e, index)}
              className={`p-4 flex items-center gap-4 transition-all cursor-move hover:bg-gray-50 ${
                !item.visible ? 'opacity-50' : ''
              } ${
                draggedItem === index ? 'opacity-40 scale-95' : ''
              } ${
                draggedOverItem === index && !draggedSubmenu ? 'border-t-4 border-blue-500 bg-blue-50' : ''
              } ${
                draggedSubmenu ? 'border-2 border-dashed border-green-500' : ''
              }`}
            >
              {/* Drag Handle */}
              <div className="flex flex-col gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="h-6 w-6 p-0"
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <GripVertical className="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing" />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => moveDown(index)}
                  disabled={index === menuItems.length - 1}
                  className="h-6 w-6 p-0"
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </div>

              {/* Order Number */}
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <span className="text-sm font-bold text-blue-600">{index + 1}</span>
              </div>

              {/* Expand Button (if has submenu) */}
              {(item.hasSubmenu || (item.submenu && item.submenu.length > 0)) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleExpand(item.id)}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight 
                    className={`w-5 h-5 transition-transform ${expandedMenus.includes(item.id) ? 'rotate-90' : ''}`} 
                  />
                </Button>
              )}

              {/* Menu Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Input
                    value={item.name}
                    onChange={(e) => updateName(item.id, e.target.value)}
                    className="max-w-xs rounded-lg"
                  />
                  {item.hasSubmenu && item.submenu && item.submenu.length > 0 && (
                    <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                      {item.submenu.length} submenús
                    </span>
                  )}
                  {item.isCustom && (
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                      Personalizado
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">ID: {item.id}</p>
              </div>

              {/* Visibility Toggle */}
              <div className="flex items-center gap-3">
                {/* Mover menú principal a otro menú como submenú */}
                <select
                  className="text-xs px-2 py-1 border rounded"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      convertMenuToSubmenu(item.id, e.target.value);
                    }
                  }}
                >
                  <option value="">Mover a...</option>
                  {menuItems
                    .filter(m => m.id !== item.id)
                    .map(m => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))
                  }
                </select>

                {item.isCustom && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteMenu(item.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <div className="flex items-center gap-2">
                  {item.visible ? (
                    <Eye className="w-4 h-4 text-green-600" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  )}
                  <Label className="text-sm">
                    {item.visible ? 'Visible' : 'Oculto'}
                  </Label>
                </div>
                <Switch
                  checked={item.visible}
                  onCheckedChange={() => toggleVisibility(item.id)}
                />
              </div>
            </div>

            {/* Submenús */}
            {(item.hasSubmenu || (item.submenu && item.submenu.length > 0)) && expandedMenus.includes(item.id) && item.submenu && (
              <div className="bg-gray-50 border-t border-gray-200">
                {item.submenu.map((sub, subIndex) => (
                  <div 
                    key={sub.id}
                    draggable
                    onDragStart={(e) => handleSubmenuDragStart(e, index, subIndex)}
                    onDragOver={handleSubmenuDragOver}
                    onDrop={(e) => handleSubmenuDrop(e, index, subIndex)}
                    onDragEnd={handleSubmenuDragEnd}
                    className={`p-4 pl-20 flex items-center gap-4 border-b border-gray-200 last:border-b-0 transition-all cursor-move hover:bg-gray-100 ${
                      !sub.visible ? 'opacity-50' : ''
                    } ${
                      draggedSubmenu?.parentIndex === index && draggedSubmenu?.submenuIndex === subIndex 
                        ? 'opacity-40 scale-95' 
                        : ''
                    }`}
                  >
                    {/* Submenu Controls */}
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => moveSubmenuUp(index, subIndex)}
                        disabled={subIndex === 0}
                        className="h-6 w-6 p-0"
                      >
                        <ChevronUp className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => moveSubmenuDown(index, subIndex)}
                        disabled={subIndex === item.submenu.length - 1}
                        className="h-6 w-6 p-0"
                      >
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </div>

                    {/* Submenu Number */}
                    <div className="w-6 h-6 rounded bg-purple-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-purple-600">{subIndex + 1}</span>
                    </div>

                    {/* Submenu Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-700">{sub.name}</span>
                        <span className="text-xs text-gray-500">Submenú de {item.name}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">ID: {sub.id}</p>
                    </div>

                    {/* Mover a otro menú */}
                    <select
                      className="text-xs px-2 py-1 border rounded"
                      value={item.id}
                      onChange={(e) => {
                        if (e.target.value !== item.id) {
                          moveSubmenuToParent(item.id, sub.id, e.target.value);
                        }
                      }}
                    >
                      <option value={item.id}>Mantener aquí</option>
                      {menuItems
                        .filter(m => m.id !== item.id) // Todos los menús pueden recibir submenús
                        .map(m => (
                          <option key={m.id} value={m.id}>
                            Mover a {m.name}
                          </option>
                        ))
                      }
                    </select>

                    {/* Submenu Visibility */}
                    <div className="flex items-center gap-2">
                      {sub.visible ? (
                        <Eye className="w-3 h-3 text-green-600" />
                      ) : (
                        <EyeOff className="w-3 h-3 text-gray-400" />
                      )}
                      <Switch
                        checked={sub.visible}
                        onCheckedChange={() => toggleSubmenuVisibility(item.id, sub.id)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </Card>

      <div className="mt-6 p-4 bg-blue-50 rounded-xl">
        <h3 className="font-semibold text-blue-900 mb-2">💡 Instrucciones</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Arrastra y suelta</strong> los menús para reordenarlos fácilmente</li>
          <li>• También puedes usar las flechas ↑↓ para cambiar el orden</li>
          <li>• <strong>Arrastrar submenús</strong>: Puedes arrastrar submenús entre diferentes menús principales</li>
          <li>• <strong>Convertir a menú principal</strong>: Arrastra un submenú sobre la zona de un menú principal (borde verde) para convertirlo</li>
          <li>• <strong>Crear Menú</strong> para agregar menús personalizados</li>
          <li>• <strong>Mover a...</strong> dropdown para convertir un menú en submenú de otro</li>
          <li>• Los menús personalizados pueden ser eliminados con el botón 🗑️</li>
          <li>• Activa/desactiva el switch para mostrar u ocultar un menú</li>
          <li>• Edita el nombre del menú principal directamente en el campo de texto</li>
          <li>• Expande los menús con submenús para gestionarlos (reordenar, ocultar, mover)</li>
          <li>• Usa el dropdown en cada submenú para moverlo a otro menú</li>
          <li>• Presiona &quot;Guardar Cambios&quot; para aplicar la configuración</li>
          <li>• Usa &quot;Restaurar&quot; para volver a la configuración original</li>
        </ul>
      </div>

      {/* Dialog para crear menú */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Menú</DialogTitle>
            <DialogDescription>
              Agrega un nuevo menú personalizado a la barra de navegación
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="menu-name">Nombre del Menú *</Label>
              <Input
                id="menu-name"
                placeholder="Ej: Configuración, Estadísticas, etc."
                value={newMenu.name}
                onChange={(e) => setNewMenu({ ...newMenu, name: e.target.value })}
                className="rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="menu-icon">Ícono (nombre de Lucide)</Label>
              <Input
                id="menu-icon"
                placeholder="Ej: Settings, BarChart, etc."
                value={newMenu.icon}
                onChange={(e) => setNewMenu({ ...newMenu, icon: e.target.value })}
                className="rounded-lg"
              />
              <p className="text-xs text-gray-500">
                Ver iconos disponibles en: <a href="https://lucide.dev/icons" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">lucide.dev/icons</a>
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setNewMenu({ name: '', icon: 'Settings' });
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={createNewMenu}
              className="bg-gradient-to-r from-green-500 to-emerald-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear Menú
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuConfigTab;
